import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    DragDropContext,
    Draggable,
    Droppable,
    type DropResult,
} from '@hello-pangea/dnd';
import { format, isValid } from 'date-fns';

import { parseBackendDate } from '@/utils/date_utils';
import type { LeadNextStep, LeadNextStepStatus } from '@/types';

export type NextStepDraft = {
    id?: number;
    clientKey: string;
    name: string;
    notify: boolean;
    notify_at: string | null;
    status: LeadNextStepStatus;
};

let nextClientKey = 0;

export function newStepClientKey(): string {
    nextClientKey += 1;
    return `new-${nextClientKey}`;
}

export function blankNextStep(): NextStepDraft {
    return {
        clientKey: newStepClientKey(),
        name: '',
        notify: false,
        notify_at: null,
        status: 'pending',
    };
}

export function toDatetimeLocalValue(date: string | null | undefined): string {
    if (!date) return '';
    const parsed = parseBackendDate(date);
    if (!isValid(parsed)) return '';
    return format(parsed, "yyyy-MM-dd'T'HH:mm");
}

export function fromDatetimeLocalValue(value: string): string | null {
    const trimmed = value.trim();
    return trimmed || null;
}

export function stepsFromRecord(steps: LeadNextStep[] | undefined): NextStepDraft[] {
    return [...(steps || [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(step => ({
            id: typeof step.id === 'number' ? step.id : undefined,
            clientKey:
                typeof step.id === 'number' ? `id-${step.id}` : newStepClientKey(),
            name: step.name,
            notify: step.notify,
            notify_at: step.notify_at ?? null,
            status: step.status || 'pending',
        }));
}

function reorder<T>(list: T[], from: number, to: number): T[] {
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
}

export const LeadNextStepsEditor = ({
    value,
    onChange,
}: {
    value: NextStepDraft[];
    onChange: (next: NextStepDraft[]) => void;
}) => {
    const update = (index: number, patch: Partial<NextStepDraft>) => {
        onChange(value.map((step, i) => (i === index ? { ...step, ...patch } : step)));
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;
        onChange(reorder(value, result.source.index, result.destination.index));
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle1">Next steps</Typography>
            {value.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No follow-up tasks yet.
                </Typography>
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="lead-next-steps">
                        {provided => (
                            <Stack
                                spacing={2}
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {value.map((step, index) => (
                                    <Draggable
                                        key={step.clientKey}
                                        draggableId={step.clientKey}
                                        index={index}
                                    >
                                        {(drag, snapshot) => (
                                            <Paper
                                                ref={drag.innerRef}
                                                {...drag.draggableProps}
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderLeftWidth: 4,
                                                    borderLeftColor: 'primary.main',
                                                    bgcolor: snapshot.isDragging
                                                        ? 'action.hover'
                                                        : undefined,
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="flex-start"
                                                    >
                                                        <Box
                                                            {...drag.dragHandleProps}
                                                            aria-label="Reorder step"
                                                            sx={{
                                                                mt: 1.25,
                                                                display: 'flex',
                                                                cursor: 'grab',
                                                                color: 'action.active',
                                                            }}
                                                        >
                                                            <DragIndicatorIcon />
                                                        </Box>
                                                        <Chip
                                                            label={index + 1}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ mt: 1.25, minWidth: 32 }}
                                                        />
                                                        <TextField
                                                            label="Task"
                                                            value={step.name}
                                                            onChange={e =>
                                                                update(index, {
                                                                    name: e.target.value,
                                                                })
                                                            }
                                                            fullWidth
                                                        />
                                                        <IconButton
                                                            aria-label="Remove step"
                                                            onClick={() =>
                                                                onChange(
                                                                    value.filter(
                                                                        (_, i) => i !== index
                                                                    )
                                                                )
                                                            }
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        spacing={2}
                                                        alignItems="center"
                                                        flexWrap="wrap"
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={step.notify}
                                                                    onChange={e =>
                                                                        update(index, {
                                                                            notify: e.target
                                                                                .checked,
                                                                            notify_at: e.target
                                                                                .checked
                                                                                ? step.notify_at
                                                                                : null,
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Reminder"
                                                        />
                                                        {step.notify ? (
                                                            <TextField
                                                                label="Remind at"
                                                                type="datetime-local"
                                                                value={toDatetimeLocalValue(
                                                                    step.notify_at
                                                                )}
                                                                onChange={e =>
                                                                    update(index, {
                                                                        notify_at:
                                                                            fromDatetimeLocalValue(
                                                                                e.target.value
                                                                            ),
                                                                    })
                                                                }
                                                                InputLabelProps={{ shrink: true }}
                                                                size="small"
                                                            />
                                                        ) : null}
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Stack>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
            <Button
                startIcon={<AddIcon />}
                onClick={() => onChange([...value, blankNextStep()])}
                sx={{ alignSelf: 'flex-start' }}
            >
                Add step
            </Button>
        </Stack>
    );
};
